import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Globe, Loader2 } from 'lucide-react'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { api, ApiError } from '@/lib/api'
import type { PrivacyConsent, PrivacyReview } from '@/types/share'


interface ShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (consent: PrivacyConsent) => Promise<void>
  isLoading: boolean
  generationId: string
  imageUrl: string
  s3Key: string
}

// Unmount each sharing session: a late review can never publish another target.
export function ShareDialog(props: ShareDialogProps) {
  const { i18n } = useTranslation()
  return props.open ? <ShareSession key={i18n.resolvedLanguage + props.generationId + props.s3Key} {...props} /> : null
}

function ShareSession(props: ShareDialogProps) {
  const { t, i18n } = useTranslation()
  const [review, setReview] = useState<PrivacyReview | null>(null)
  const [checking, setChecking] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const alive = useRef(true)
  const submitting = useRef(false)
  const publishing = useRef(false)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])
  const warning = review !== null && review.outcome !== 'clear'

  function changeOpen(open: boolean) {
    if (props.isLoading || publishing.current) return
    if (!open) alive.current = false
    props.onOpenChange(open)
  }

  async function confirm() {
    if (submitting.current) return
    submitting.current = true
    setError(null)
    try {
      let current = review
      if (!current || Date.parse(current.expiresAt) <= Date.now()) {
        setReview(null)
        setAcknowledged(false)
        setChecking(true)
        current = await api.post<PrivacyReview>('/share/privacy-check', {
          generationId: props.generationId,
          s3Key: props.s3Key,
          language: i18n.resolvedLanguage ?? 'ko',
        })
        if (!alive.current) return
        setReview(current)
        setChecking(false)
        if (current.outcome !== 'clear') return
      }
      if (!alive.current || (current.outcome !== 'clear' && !acknowledged)) return
      publishing.current = true
      await props.onConfirm({ privacyReviewId: current.reviewId, privacyAcknowledged: current.outcome !== 'clear' && acknowledged })
    } catch (cause) {
      if (!alive.current) return
      setError(cause instanceof Error ? cause.message : t('history.shareFailed'))
      if (cause instanceof ApiError && cause.code === 'SHARE_PRIVACY_CHECK_REQUIRED') {
        setReview(null)
        setAcknowledged(false)
      }
    } finally {
      submitting.current = false
      publishing.current = false
      if (alive.current) setChecking(false)
    }
  }

  return (
    <Dialog open onOpenChange={changeOpen}>
      <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto"
        onEscapeKeyDown={(event) => { if (props.isLoading) event.preventDefault() }}
        onPointerDownOutside={(event) => { if (props.isLoading) event.preventDefault() }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {warning ? <AlertTriangle className="h-5 w-5 text-amber-700" /> : <Globe className="h-5 w-5 text-primary" />}
            {warning ? t('share.privacy.title') : t('share.dialogTitle')}
          </DialogTitle>
          <DialogDescription>
            <Trans i18nKey="share.dialogDescription" components={{ strong: <strong className="font-semibold" /> }} />
            <br />
            <Trans i18nKey="share.dialogNotice" components={{ strong: <strong className="font-semibold" /> }} />
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center py-2">
          <img src={props.imageUrl} alt={t('share.previewAlt')} className="max-h-48 max-w-full rounded-lg border border-slate-200" />
        </div>
        {checking && <p role="status" className="flex items-center gap-2 text-sm text-slate-700"><Loader2 className="h-4 w-4 shrink-0 animate-spin" />{t('share.privacy.checking')}</p>}
        {warning && (
          <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <p role="status" className="font-medium">{t(review.outcome === 'unavailable' ? 'share.privacy.unavailable' : 'share.privacy.detected')}</p>
            {review.findings.length > 0 && <ul className="list-disc space-y-1 pl-5">
              {review.findings.map((finding) => <li key={`${finding.source}:${finding.index}:${finding.category}`}>
                {t('share.privacy.finding', {
                  location: t(`share.privacy.sources.${finding.source}`, { index: finding.index }),
                  category: t(`share.privacy.categories.${finding.category}`),
                })}
              </li>)}
            </ul>}
            <label className="flex cursor-pointer items-start gap-2">
              <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} disabled={props.isLoading}
                className="mt-0.5 h-4 w-4 shrink-0 accent-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary" />
              <span>{t('share.privacy.consent')}</span>
            </label>
          </div>
        )}
        <p className="text-xs leading-relaxed text-slate-600">{t('share.privacy.limitation')} </p>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => changeOpen(false)} disabled={props.isLoading}>{t('common.cancel')}</Button>
          <Button onClick={() => void confirm()} disabled={checking || props.isLoading || (warning && !acknowledged)}>
            {(checking || props.isLoading) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {checking ? t('share.privacy.checkingShort') : props.isLoading ? t('share.sharing') : warning ? t('share.privacy.publish') : t('share.shareButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
